<script setup lang="ts">

import {teamApi, teamPlanningApi} from "@/container";
import {computed, onMounted, ref, useTemplateRef, watch} from "vue";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamAbsence, TeamMemberSchedule} from "@/core/interfaces/TeamPlanning";
import BaseCard from "@/core/components/base/BaseCard.vue";
import {pluralize} from "@/core/filters/Pluralize";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import TeamMemberList from "@/team/components/TeamMemberList.vue";
import MemberProfileCard from "@/team/components/MemberProfileCard.vue";
import RecurrenceBuilder from "@/team/components/RecurrenceBuilder.vue";
import MemberAbsenceHistory from "@/team/components/MemberAbsenceHistory.vue";
import Rank from "@/core/interfaces/Rank";
import {onBeforeRouteLeave, useRoute, useRouter} from "vue-router";
import NewFeatureTag from "@/core/components/tags/NewFeatureTag.vue";
import {Play} from "@lucide/vue";
import VideoItem from "@/video/components/VideoItem.vue";
import useVideos from "@/video/composables/useVideos";
import useTeamSchedule from "@/team/composables/useTeamSchedule";
import AssistantLauncher from "@/ai-assistant/components/AssistantLauncher.vue";
import useAssistantScreen from "@/ai-assistant/composables/useAssistantScreen";
import useExperimentalFeatures from "@/core/composables/experimental-features/useExperimentalFeatures";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";

const team = ref<LegacyAccess[]>();
const ranks = ref<Rank[]>([]);
const operatorCodeOptions = ref<{ label: string; value: string }[]>([]);
const absences = ref<TeamAbsence[]>([]);
const selectedId = ref<number | null>(null);
const loading = ref<boolean>(true);
const router = useRouter();

// A ?member=<id> query (e.g. the planning's pencil icon) preselects that
// member on arrival; consumed once, later refreshes keep the user's choice.
const requestedMemberId = Number(useRoute().query.member) || null;
const {schedulesByAccessId, upsertSchedule, organizationId} = useTeamSchedule();
const {data: videos} = useVideos({
    limit: 1,
    tags: ['team-tutorial']
});

const selectedMember = computed(() =>
    team.value?.find(member => member.id === selectedId.value) ?? null
);

const selectedSchedule = computed(() =>
    selectedId.value ? schedulesByAccessId.value.get(selectedId.value) ?? null : null
);

/**
 * What the assistant is told about this screen.
 *
 * The first name of the collaborator whose fiche is open, and nothing more:
 * it is what the pharmacist is looking at, and it is already what the read
 * tools return. Their absences are shown here too, but no motive travels —
 * none ever reaches this screen either.
 */
useAssistantScreen(() => ({
    screen: 'Équipe',
    collaborator: selectedMember.value?.user?.name ?? null
}));

/** The assistant is offered here only in experimental mode (Ctrl+X) for now. */
const {enabled: assistantOffered} = useExperimentalFeatures();

onMounted(() => {
    Promise.all([fetchTeam(), fetchRanks(), fetchOperatorCodes()])
        .finally(() => loading.value = false);
});

/**
 * Fetch team, keeping the selection on a still-existing member.
 */
const fetchTeam = () => {
    return teamApi().index()
        .then(response => {
            team.value = response.team;

            if (selectedId.value === null && requestedMemberId && team.value?.some(member => member.id === requestedMemberId)) {
                selectedId.value = requestedMemberId;
            } else if (!team.value?.some(member => member.id === selectedId.value)) {
                selectedId.value = team.value?.[0]?.id ?? null;
            }
        });
};

/**
 * Fetch healthcenter ranks.
 */
const fetchRanks = () => {
    return teamApi().rankList()
        .then(response => {
            if (response) {
                ranks.value = response;
            }
        });
};

/**
 * Fetch healthcenter operator codes. We filter potential empty string because it breaks the select.
 */
const fetchOperatorCodes = () => {
    return teamApi().operatorCodes()
        .then(response => {
            if (response) {
                operatorCodeOptions.value = [{label: "Aucun", value: "null"}, ...response.map(item => {
                    return {label: item, value: item};
                })]
                    .filter(item => item.value.length > 0);
            }
        });
};

/**
 * Fetch the absences of the organization, for the member history section.
 * Bounded to the last twelve months so the payload stays constant however
 * long the pharmacy has used the feature.
 */
const fetchAbsences = () => {
    if (!organizationId.value) {
        return;
    }

    const from = new Date();

    from.setMonth(from.getMonth() - 12);

    teamPlanningApi().absences(organizationId.value, {from: from.toISOString().slice(0, 10)})
        .then(response => absences.value = response);
};

watch(organizationId, () => fetchAbsences(), {immediate: true});

/**
 * Remove an affected operator code from a team member.
 *
 * @param {string} operatorCode - Operator code.
 */
const removeOperatorCodeFromMember = (operatorCode: string) => {
    if (team.value) {
        const member = team.value.find(member => member.operatorCode === operatorCode);

        if (member) {
            member.operatorCode = "";
            teamApi().updateAccess(member)
                .finally(() => fetchTeam());
        }
    }
};

/**
 * A profile or shifts save returned the fresh schedule: write it straight
 * into the cache, sparing a full org refetch per save.
 */
const onScheduleUpdated = (updated: TeamMemberSchedule) => {
    upsertSchedule(updated);
};

// ----- Unsaved recurring schedule: nothing the pharmacist edited may be lost
// without them being asked, whether they switch member or leave the screen. -----

const scheduleDirty = ref<boolean>(false);
const recurrence = useTemplateRef<InstanceType<typeof RecurrenceBuilder>>('recurrence');
const leaveDialogOpen = ref<boolean>(false);
const savingBeforeLeave = ref<boolean>(false);

/** What the pharmacist asked for, held until they say what to do with the edits. */
let pendingLeave: (() => void) | null = null;

/** Set once the pharmacist chose to leave, so the replayed navigation passes the guard. */
let leaveConfirmed = false;

const guardLeave = (proceed: () => void) => {
    if (!scheduleDirty.value) {
        proceed();

        return;
    }

    pendingLeave = proceed;
    leaveDialogOpen.value = true;
};

const selectMember = (id: number) => {
    if (id !== selectedId.value) {
        guardLeave(() => selectedId.value = id);
    }
};

onBeforeRouteLeave(to => {
    if (leaveConfirmed || !scheduleDirty.value) {
        return true;
    }

    guardLeave(() => {
        leaveConfirmed = true;
        router.push(to.fullPath).finally(() => leaveConfirmed = false);
    });

    return false;
});

const stay = () => {
    pendingLeave = null;
    leaveDialogOpen.value = false;
};

/**
 * Switching member rebuilds the grid from the saved schedule, and leaving the
 * screen unmounts it: either way the edits are dropped by the move itself.
 */
const leaveWithoutSaving = () => {
    const proceed = pendingLeave;

    stay();
    proceed?.();
};

/**
 * A failed save keeps the pharmacist on the fiche, edits intact: the save
 * has already said why in a toast.
 */
const saveAndLeave = async () => {
    const proceed = pendingLeave;

    savingBeforeLeave.value = true;
    const saved = await recurrence.value?.save() ?? false;
    savingBeforeLeave.value = false;

    stay();

    if (saved) {
        proceed?.();
    }
};
</script>

<template>
  <BaseFlexSpinner v-if="loading" />
  <div v-else class="p-4">
    <BaseCard class="border-2 border-primary">
      <div class="flex flex-row justify-between items-start gap-4 px-5 pt-5 pb-4">
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <h2 class="text-gray-800 font-semibold text-lg">
              Mon équipe
            </h2>
            <span class="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
              {{ team?.length || 0 }} {{ pluralize('membre', team?.length || 0) }}
            </span>
          </div>
          <p class="text-sm text-gray-500">
            Gérez les fiches, horaires récurrents et absences de votre équipe
          </p>
          <div class="flex gap-3">
            <div
              v-for="video in (videos?.data ?? [])" :key="video.id"
              class="hover:bg-lightPurple-lightest rounded-lg transition duration-200 p-1"
            >
              <VideoItem :video="video" class="bg-[#FB0129] text-white py-1">
                <template #icon>
                  <Play class="fill-white shrink-0 size-5 ml-2" />
                </template>
              </VideoItem>
            </div>
          </div>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <AssistantLauncher v-if="assistantOffered" />
          <BaseButton primary @click="router.push('/planning')">
            Voir le planning
          </BaseButton>
        </div>
      </div>
      <div class="mx-5 mb-4 flex items-start gap-2.5 rounded-lg border border-primary/15 bg-primary/5 px-3.5 py-2.5">
        <NewFeatureTag class="h-5 shrink-0" />
        <p class="text-xs leading-relaxed text-primary/80">
          Préremplissage des bons de prise en charge vaccin : disponible pour les co-titulaires et assistants.
        </p>
      </div>

      <div class="grid grid-cols-[280px_1fr] items-start gap-4 px-5 pb-5">
        <TeamMemberList
          :team="team ?? []"
          :schedules="schedulesByAccessId"
          :selected-id="selectedId"
          :ranks="ranks"
          :operator-code-options="operatorCodeOptions"
          @select="selectMember"
          @refresh="fetchTeam"
          @schedule-updated="onScheduleUpdated"
          @on-remove-operator-code="removeOperatorCodeFromMember"
        />

        <div v-if="selectedMember && organizationId" class="flex flex-col gap-4">
          <MemberProfileCard
            :member="selectedMember"
            :schedule="selectedSchedule"
            :organization-id="organizationId"
            :team="team ?? []"
            :ranks="ranks"
            :operator-code-options="operatorCodeOptions"
            @schedule-updated="onScheduleUpdated"
            @refresh="fetchTeam"
            @on-remove-operator-code="removeOperatorCodeFromMember"
          />
          <RecurrenceBuilder
            ref="recurrence"
            v-model:dirty="scheduleDirty"
            :schedule="selectedSchedule"
            :member-id="selectedMember.id"
            :organization-id="organizationId"
            @schedule-updated="onScheduleUpdated"
          />
          <MemberAbsenceHistory
            :absences="absences"
            :member-id="selectedMember.id"
          />
        </div>
        <div
          v-else
          class="flex items-center justify-center rounded-xl border border-dashed border-gray-200 p-10 text-sm text-gray-400"
        >
          Sélectionnez un collaborateur pour afficher sa fiche.
        </div>
      </div>
    </BaseCard>
    <AlertDialog
      :open="leaveDialogOpen"
      @update:open="open => { if (!open && !savingBeforeLeave) stay() }"
    >
      <AlertDialogContent class="max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Modifications non enregistrées</AlertDialogTitle>
          <AlertDialogDescription>
            Le planning récurrent{{ selectedMember?.user?.name ? ` de ${selectedMember.user.name}` : '' }}
            a été modifié sans être enregistré. Voulez-vous l'enregistrer avant de continuer ?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter class="gap-2 sm:flex-wrap">
          <AlertDialogCancel :disabled="savingBeforeLeave">
            Rester
          </AlertDialogCancel>
          <BaseButton variant="text" :disabled="savingBeforeLeave" @click="leaveWithoutSaving">
            Quitter sans enregistrer
          </BaseButton>
          <BaseButton primary :loading="savingBeforeLeave" @click="saveAndLeave">
            Enregistrer et continuer
          </BaseButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <BaseCard class="p-4 mt-4">
      <div class="items-center justify-center flex flex-col">
        <img src="@/assets/images/challenge_teaser.png" class="rounded-[10px]">
      </div>
      <div class="flex flex-row justify-end items-center gap-5 py-4 pr-12">
        <BaseButton primary @click="() => router.push('contests')">
          Voir les challenges
        </BaseButton>
      </div>
    </BaseCard>
  </div>
</template>

<style scoped>
:deep(.video-title) {
    @apply text-sm text-white font-medium leading-tight;
}
</style>
